import React from 'react';
import { CheckCircle2, Award, Clock, Shield, Star } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const engagements = [
  {
    icon: Clock,
    title: 'Réactivité',
    description: 'Intervention rapide dans les plus brefs délais pour limiter les dégâts.'
  },
  {
    icon: Shield,
    title: 'Fiabilité',
    description: 'Méthodes éprouvées et rapports détaillés pour une prise en charge efficace.'
  },
  {
    icon: Star,
    title: 'Qualité',
    description: 'Standards élevés et amélioration continue de nos prestations.'
  }
];

const About = () => {
  useSEO({
    title: "Qui sommes-nous ? | Expomis, expertise et recherche de fuite",
    description: "Plus de 20 ans d'expérience en expertise d'assurance et recherche de fuite au Luxembourg. Découvrez notre équipe certifiée et nos engagements qualité.",
    path: '/qui-sommes-nous'
  });

  return (
    <div className="pt-40 pb-24">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-4">
            Qui sommes-nous
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-6">
            Une expertise construite sur la rigueur
          </h1>
          <p className="text-lg text-ink-soft leading-relaxed">
            Notre société, spécialisée en expertise dans le domaine de l'assurance et de la recherche de fuite,
            met à disposition une équipe d'experts qualifiés pour répondre aux besoins spécifiques de chaque client.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-24">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-6">Notre expertise</h2>
            <p className="text-ink-soft leading-relaxed mb-8">
              Grâce à nos années d'expérience et notre approche rigoureuse, nous vous accompagnons
              pour sécuriser vos intérêts et minimiser vos risques. Nous intervenons aussi bien comme
              experts mandatés par les compagnies d'assurance que comme contre-expert indépendant au
              service des assurés, en combinant savoir-faire traditionnel et technologies modernes
              pour des résultats optimaux.
            </p>
            <div className="space-y-4">
              {[
                "Plus de 20 ans d'expérience dans le domaine",
                'Équipe certifiée et régulièrement formée',
                'Technologies de pointe non destructives',
                "Partenariats avec les principales compagnies d'assurance",
                'Contre-expertise indépendante disponible pour les assurés'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                  <span className="text-ink-soft">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-line p-10">
            <div className="w-14 h-14 flex items-center justify-center border border-brand/30 mb-6">
              <Award className="w-7 h-7 text-brand" strokeWidth={1.4} />
            </div>
            <h3 className="font-serif text-2xl font-semibold text-ink mb-4">Excellence garantie</h3>
            <p className="text-ink-soft leading-relaxed">
              Un accompagnement personnalisé pour sécuriser vos biens et minimiser vos risques
              avec une satisfaction client garantie.
            </p>
          </div>
        </div>

        <div>
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-12 text-center">
            Nos engagements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3">
            {engagements.map((engagement, index) => (
              <div
                key={index}
                className={`text-center px-8 py-2 ${
                  index > 0 ? 'md:border-l md:border-line' : ''
                }`}
              >
                <div className="w-12 h-12 mx-auto mb-5 flex items-center justify-center border border-brand/30">
                  <engagement.icon className="w-6 h-6 text-brand" strokeWidth={1.4} />
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-2">{engagement.title}</h3>
                <p className="text-ink-soft leading-relaxed">{engagement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
